/**
 * The signed release pointer, as the browser sees it.
 *
 * The verified bootstrap never names a core release itself. It fetches
 * pointer/v1.json, a small mutable object the release pipeline moves on every
 * publish, and only injects the core that object names once it has proven the
 * object was signed by a Pritect release key. So a release reaches a site with
 * no snippet change, while bucket write access alone can no longer choose what
 * runs there: an attacker who can upload files but cannot sign can at most make
 * the bootstrap fail closed.
 *
 * The signing half lives in scripts/lib/pointer-signing.mjs. The tests sign
 * with that module and verify with this one, so the two cannot drift apart.
 *
 * Wire format (pointer/v1.json):
 *   { schemaVersion: 1, keyId, payload: base64(JSON bytes), signature }
 * The signature is ECDSA P-256 with SHA-256, in the raw r||s form WebCrypto
 * expects, over POINTER_CONTEXT followed by the payload bytes. Signing the
 * exact bytes rather than a re-serialised object means there is no JSON
 * canonicalisation for the two sides to disagree about.
 */

export const POINTER_CONTEXT = "pritect-beacon/release-pointer/v1\n";

/** Visitors' clocks are wrong in both directions. Past this, expiry is enforced. */
export const EXPIRY_GRACE_MS = 24 * 60 * 60 * 1000;

/** A pointer is a few hundred bytes. Anything far larger is not ours. */
const MAX_ENVELOPE_BYTES = 8 * 1024;

const CORE_PATH = /^releases\/pritect-beacon\.[0-9a-f]{12}\.min\.js$/;
const SRI_SHA384 = /^sha384-[A-Za-z0-9+/]{64}$/;

const CACHE_KEY = "pb_ptr_v1";
const HIGH_WATER_KEY = "pb_ptr_hw_v1";

export interface PointerPayload {
  /** Bucket path of the core, always an immutable content-hashed release. */
  core: string;
  /** SRI for the core, enforced by the browser when it is injected. */
  integrity: string;
  issuedAt: string;
  expiresAt: string;
  /** Oldest bootstrap major that may run this core. */
  minBootstrap: number;
}

/** Public half of a release key, as committed in release-pointer.json. */
export interface PointerKey {
  kid: string;
  kty: string;
  crv: string;
  x: string;
  y: string;
}

export type PointerFailure =
  | "malformed"
  | "unknown_key"
  | "bad_signature"
  | "invalid_payload"
  | "expired"
  | "rollback"
  | "bootstrap_outdated";

export class PointerError extends Error {
  constructor(readonly reason: PointerFailure, message: string) {
    super(message);
  }
}

export interface VerifyOptions {
  subtle: SubtleCrypto;
  bootstrapMajor: number;
  /** issuedAt (ms) of the newest pointer this browser has accepted, or 0. */
  highWater: number;
  now?: number;
}

function decodeBase64(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parsePayload(bytes: Uint8Array): PointerPayload {
  let parsed: unknown;
  try {
    parsed = JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    throw new PointerError("invalid_payload", "Release pointer payload is not JSON.");
  }
  if (!isRecord(parsed)) throw new PointerError("invalid_payload", "Release pointer payload is not an object.");
  const { core, integrity, issuedAt, expiresAt, minBootstrap } = parsed;
  if (typeof core !== "string" || !CORE_PATH.test(core)) {
    throw new PointerError("invalid_payload", "Release pointer names a core outside the immutable release channel.");
  }
  if (typeof integrity !== "string" || !SRI_SHA384.test(integrity)) {
    throw new PointerError("invalid_payload", "Release pointer carries no usable sha384 integrity.");
  }
  if (typeof issuedAt !== "string" || !Number.isFinite(Date.parse(issuedAt))) {
    throw new PointerError("invalid_payload", "Release pointer has no valid issuedAt.");
  }
  if (typeof expiresAt !== "string" || !Number.isFinite(Date.parse(expiresAt))) {
    throw new PointerError("invalid_payload", "Release pointer has no valid expiresAt.");
  }
  if (typeof minBootstrap !== "number" || !Number.isInteger(minBootstrap) || minBootstrap < 1) {
    throw new PointerError("invalid_payload", "Release pointer has no valid minBootstrap.");
  }
  return { core, integrity, issuedAt, expiresAt, minBootstrap };
}

/**
 * Verify a pointer and return its payload, or throw a PointerError.
 *
 * Order matters: nothing inside the payload is looked at until the signature
 * over it has verified, so an unsigned object cannot even choose which error
 * the page reports.
 */
export async function verifyPointer(
  text: string,
  keys: readonly PointerKey[],
  opts: VerifyOptions,
): Promise<PointerPayload> {
  if (text.length > MAX_ENVELOPE_BYTES) throw new PointerError("malformed", "Release pointer is too large.");

  let envelope: unknown;
  try {
    envelope = JSON.parse(text);
  } catch {
    throw new PointerError("malformed", "Release pointer is not JSON.");
  }
  if (
    !isRecord(envelope) ||
    envelope["schemaVersion"] !== 1 ||
    typeof envelope["keyId"] !== "string" ||
    typeof envelope["payload"] !== "string" ||
    typeof envelope["signature"] !== "string"
  ) {
    throw new PointerError("malformed", "Release pointer is not a signed pointer envelope.");
  }

  const jwk = keys.find((k) => k.kid === envelope["keyId"]);
  if (!jwk) throw new PointerError("unknown_key", `Release pointer is signed by an unknown key (${envelope["keyId"]}).`);

  let payloadBytes: Uint8Array<ArrayBuffer>;
  let signature: Uint8Array<ArrayBuffer>;
  try {
    payloadBytes = decodeBase64(envelope["payload"]);
    signature = decodeBase64(envelope["signature"]);
  } catch {
    throw new PointerError("malformed", "Release pointer is not valid base64.");
  }

  const context = new TextEncoder().encode(POINTER_CONTEXT);
  const message = new Uint8Array(context.length + payloadBytes.length);
  message.set(context, 0);
  message.set(payloadBytes, context.length);

  let valid = false;
  try {
    const key = await opts.subtle.importKey(
      "jwk",
      { kty: jwk.kty, crv: jwk.crv, x: jwk.x, y: jwk.y, ext: true },
      { name: "ECDSA", namedCurve: "P-256" },
      false,
      ["verify"],
    );
    valid = await opts.subtle.verify({ name: "ECDSA", hash: "SHA-256" }, key, signature, message);
  } catch {
    valid = false;
  }
  if (!valid) throw new PointerError("bad_signature", "Release pointer signature does not verify.");

  const payload = parsePayload(payloadBytes);
  const now = opts.now ?? Date.now();
  if (Date.parse(payload.expiresAt) + EXPIRY_GRACE_MS < now) {
    throw new PointerError("expired", `Release pointer expired at ${payload.expiresAt}.`);
  }
  // A signed but older pointer is exactly what a replay of a vulnerable
  // release looks like, so once a browser has seen a newer one it never goes
  // back. The pipeline signs monotonically, so this never fires on a real
  // release, a rollback included (a rollback is a NEW signature over an older
  // core, issued now).
  if (Date.parse(payload.issuedAt) < opts.highWater) {
    throw new PointerError("rollback", "Release pointer is older than one this browser already accepted.");
  }
  if (payload.minBootstrap > opts.bootstrapMajor) {
    throw new PointerError(
      "bootstrap_outdated",
      `This embed snippet is too old for the current release (needs bootstrap ${payload.minBootstrap}). Re-copy it from the Deploy tab.`,
    );
  }
  return payload;
}

/** Last pointer this browser accepted, for repeat visits. Null when unavailable. */
export function readCachedPointer(): string | null {
  try {
    return localStorage.getItem(CACHE_KEY);
  } catch {
    return null;
  }
}

/** issuedAt (ms) of the newest pointer this browser accepted, or 0. */
export function readHighWater(): number {
  try {
    const raw = Number(localStorage.getItem(HIGH_WATER_KEY));
    return Number.isFinite(raw) && raw > 0 ? raw : 0;
  } catch {
    return 0;
  }
}

/**
 * Remember an accepted pointer. Never lowers the high-water mark, so a stale
 * response that arrives late cannot re-open a rollback.
 */
export function rememberPointer(text: string, payload: PointerPayload): void {
  try {
    const issued = Date.parse(payload.issuedAt);
    if (issued < readHighWater()) return;
    localStorage.setItem(HIGH_WATER_KEY, String(issued));
    localStorage.setItem(CACHE_KEY, text);
  } catch {
    /* Storage blocked: every visit verifies from the network instead. */
  }
}
